/**
 * KPI Card (Information-Rich)
 *
 * Korat Health Style: Detailed cards with icon, description, big number, and breakdown
 */

import React from 'react'
import { EmptyState } from '../ui/atoms'

interface KpiCardRichProps {
  /** Lucide icon component */
  icon: React.ElementType
  /** Card title (Thai) */
  title: string
  /** Description text */
  description: string
  /** Main value (big number) */
  value: string | number
  /** Unit (e.g., คน, บาท) */
  unit: string
  /** Helper text below value */
  helperText?: string
  /** Breakdown items (e.g., ["เด็ก 8.6%", "วัยทำงาน 81.5%"]) */
  breakdown?: string[]
  /** Loading state */
  isLoading?: boolean
  /** Color variant */
  variant?: 'navy' | 'blue' | 'green' | 'orange' | 'red'
  /** Custom className */
  className?: string
}

export function KpiCardRich({
  icon: Icon,
  title,
  description,
  value,
  unit,
  helperText,
  breakdown,
  isLoading = false,
  variant = 'navy',
  className = '',
}: KpiCardRichProps) {
  const variantColors = {
    navy: {
      iconBg: 'bg-korat-navy-100',
      iconColor: 'text-korat-navy-700',
      valueBg: 'text-korat-navy-700',  // All numbers use Korat navy
    },
    blue: {
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-700',
      valueBg: 'text-korat-navy-700',  // All numbers use Korat navy
    },
    green: {
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-700',
      valueBg: 'text-korat-navy-700',  // All numbers use Korat navy
    },
    orange: {
      iconBg: 'bg-korat-orange-100',
      iconColor: 'text-korat-orange-700',
      valueBg: 'text-korat-navy-700',  // All numbers use Korat navy
    },
    red: {
      iconBg: 'bg-red-100',
      iconColor: 'text-red-700',
      valueBg: 'text-korat-navy-700',  // All numbers use Korat navy
    },
  }

  const colors = variantColors[variant]

  const formatNumber = (val: string | number): string => {
    const num = typeof val === 'string' ? parseFloat(val) : val
    if (isNaN(num)) return String(val)
    return new Intl.NumberFormat('th-TH', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(num)
  }

  return (
    <div className={`bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        {/* Icon container */}
        <div
          className={`w-10 h-10 rounded-lg ${colors.iconBg} flex items-center justify-center shrink-0`}
        >
          <Icon className={`w-5 h-5 ${colors.iconColor}`} aria-hidden />
        </div>

        {/* Text block */}
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-korat-navy-700 leading-tight">
            {title}
          </h3>
          <p className="text-sm text-slate-500 mt-0.5 leading-relaxed">
            {description}
          </p>
        </div>
      </div>

      {/* Big Number */}
      {isLoading ? (
        <div className="h-10 bg-slate-200 rounded animate-pulse mb-1" />
      ) : value === 0 || value === '0' ? (
        <EmptyState size="sm" message="ยังไม่มีข้อมูลในระบบ" />
      ) : (
        <div className="flex items-baseline gap-2">
          <span className={`text-3xl font-bold ${colors.valueBg} tabular-nums`}>
            {formatNumber(value)}
          </span>
          <span className="text-sm text-slate-500">{unit}</span>
        </div>
      )}

      {/* Helper Text */}
      {helperText && (
        <p className="text-xs text-slate-500 mt-1 leading-relaxed">
          {helperText}
        </p>
      )}

      {/* Breakdown */}
      {breakdown && breakdown.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600">
            {breakdown.map((item, index) => (
              <React.Fragment key={index}>
                {index > 0 && <span className="text-slate-300">•</span>}
                <span dangerouslySetInnerHTML={{ __html: item }} />
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
